import * as MobileDetect from "mobile-detect/mobile-detect";
import { LayoutMapSchema } from "slotworks/config/game-config-schema";
import canvasManager, { Orientation } from "slotworks/graphics/canvas/canvas-manager";

class DeviceInfo {
    /**
     * This will overwrite pixel density to force high-res assets to be loaded
     */
    public isDesktop: boolean;

    /**
     * This is used to determine the canvas layout / size (phone or tablet version)
     */
    public isPhone: boolean;

    public pixelDensity: number;
    /**
     * Use this for debugging only
     */
    public forcePixelDensity: number;

    public layoutId: string;

    private mobileDetect: MobileDetect;

    constructor() {

        this.mobileDetect = new MobileDetect(window.navigator.userAgent);

        this.isDesktop = this.mobileDetect.mobile() === null;
        this.isPhone = this.mobileDetect.phone() !== null;

        // iOS 13 iPad report themselves as desktop
        if (this.isIpad()) {
            this.isDesktop = false;
        }

        this.pixelDensity = window.devicePixelRatio;
    }

    public selectLayout(layoutMap: LayoutMapSchema) {
        if (this.isDesktop) {
            this.layoutId = layoutMap.desktop;
        } else if (this.isPhone) {
            this.layoutId = layoutMap.phone;
        } else {
            this.layoutId = layoutMap.tablet;
        }
    }

    public isIOSHomeScreen() {
        return this.isAppleDevice() && window && window.navigator && (window.navigator as any).standalone;
    }

    public isAppleDevice() {
        return /iPad|iPhone|iPod/.test(navigator.userAgent) || this.isIpad();
    }

    public isSafari() {
        return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
    }

    public isIpad() {
        return /iPad/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    }

    public isIphone4() {
        return this.isAppleDevice() && this.checkIfResolutionMatches(960);
    }

    public isIphone5() {
        return this.isAppleDevice() && this.checkIfResolutionMatches(1136);
    }

    public isIphoneX() {
        return this.isAppleDevice() && this.checkIfResolutionMatches(2436);
    }

    public isIphoneXr() {
        return this.isAppleDevice() && this.checkIfResolutionMatches(1792);
    }

    public isIphoneXsMax() {
        return this.isAppleDevice() && this.checkIfResolutionMatches(2688);
    }

    /**
     * Assumes Android if it's mobile and not apple
     */
    public isAndroid() {
        return !this.isDesktop && !this.isAppleDevice();
    }

    public hasTaskBar() {
        return this.isIphoneX() || this.isIphoneXr() || this.isIphoneXsMax();
    }

    public isSamsungBrowser() {
        return navigator.userAgent.toLowerCase().indexOf("samsungbrowser") > -1;
    }

    public isFireFox() {
        return navigator.userAgent.toLowerCase().indexOf("firefox") > -1;
    }

    public isiOSChrome() {
        return /CriOS/i.test(navigator.userAgent) && this.isAppleDevice();
    }

    public isIE11() {
        return this.getIEVersion() === 11;
    }

    public getIEVersion() {
        {
            let ua;
            let rv = -1;
            if (navigator.appName === "Microsoft Internet Explorer") {
                ua = navigator.userAgent;
                const re = new RegExp("MSIE ([0-9]{1,}[\.0-9]{0,})");
                if (re.exec(ua) != null) {
                    rv = parseFloat(RegExp.$1);
                }
            } else if (navigator.appName === "Netscape") {
                ua = navigator.userAgent;
                const re = new RegExp("Trident/.*rv:([0-9]{1,}[\.0-9]{0,})");
                if (re.exec(ua) != null) {
                    rv = parseFloat(RegExp.$1);
                }
            }
            return rv;
        }
    }

    /**
     * Returns true if the "chrome" (url bar, etc) around the browser is visible - i.e the device is not in "full screen". iOS only
     */
    public iOSChromeVisible() {
        if (!this.isAppleDevice()) {
            return false;
        }

        let iosFullScreen = window.innerHeight >= document.documentElement.clientHeight;
        if (canvasManager.orientation === Orientation.PORTRAIT) {
            iosFullScreen = window.innerHeight > document.documentElement.clientHeight;
            if (this.isIpad()) {
                iosFullScreen = true;
            }
        }

        return !iosFullScreen;
    }

    public getScreenAspectRatio() {
        return Math.max(this.getScreenWidth(), this.getScreenHeight()) / Math.min(this.getScreenWidth(), this.getScreenHeight());
    }

    public getScreenWidth() {
        return this.isIOSHomeScreen() || this.isSamsungBrowser() ? document.documentElement.clientWidth : window.innerWidth;
    }

    public getScreenHeight() {
        return this.isIOSHomeScreen() || this.isSamsungBrowser() ? document.documentElement.clientHeight : window.innerHeight;
    }

    // This is used only by the debug menu, and exclude iOS specific logic which is in use on develop.
    public getWindowWidth() {
        return this.getScreenWidth();
    }

    // This is used only by the debug menu, and exclude iOS specific logic which is in use on develop.
    public getWindowHeight() {
        return this.getScreenHeight();
    }

    public getOrientation(): Orientation {
        return (this.getScreenWidth() > this.getScreenHeight()) ? Orientation.LANDSCAPE : Orientation.PORTRAIT;
    }

    private checkIfResolutionMatches(resolution: number) {
        const highestSide = Math.max(window.screen.height, window.screen.width);
        const multiplier = window.devicePixelRatio || 1;

        return highestSide === resolution / multiplier;
    }
}

export default new DeviceInfo();
